import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, 
  Download, 
  Loader2,
  Users,
  Calendar,
  Clock,
  Heart,
  TrendingUp,
  Award,
  FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RendicontazioneSociale() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [volontari, setVolontari] = useState([]);
  
  // Filters
  const currentYear = new Date().getFullYear();
  const [anno, setAnno] = useState(currentYear.toString());
  const [dataInizio, setDataInizio] = useState(`${currentYear}-01-01`);
  const [dataFine, setDataFine] = useState(`${currentYear}-12-31`);
  const [selectedVolontario, setSelectedVolontario] = useState('');

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    fetchVolontari();
  }, []);

  useEffect(() => {
    setDataInizio(`${anno}-01-01`);
    setDataFine(`${anno}-12-31`);
  }, [anno]);

  const fetchVolontari = async () => {
    try {
      const response = await fetch(`${API_URL}/api/volontari`, { credentials: 'include' });
      if (response.ok) {
        setVolontari(await response.json());
      }
    } catch (error) {
      console.error('Error fetching volontari:', error);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/reports/rendicontazione-sociale?anno=${anno}&data_inizio=${dataInizio}&data_fine=${dataFine}&formato=json`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        throw new Error('Errore nel caricamento');
      }
    } catch (error) {
      toast.error('Errore nel caricamento del report');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (formato) => {
    try {
      const response = await fetch(
        `${API_URL}/api/reports/rendicontazione-sociale?anno=${anno}&data_inizio=${dataInizio}&data_fine=${dataFine}&formato=${formato}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) throw new Error('Errore download');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rendicontazione_sociale_${anno}.${formato}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report scaricato con successo');
    } catch (error) {
      toast.error('Errore durante il download');
    }
  };

  const downloadCertificato = async (volontarioId) => {
    if (!volontarioId) {
      toast.error('Seleziona un volontario');
      return;
    }
    try {
      const response = await fetch(
        `${API_URL}/api/reports/certificazione-volontario/${volontarioId}?anno=${anno}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) throw new Error('Errore download');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificato_volontario_${anno}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificato scaricato con successo');
    } catch (error) {
      toast.error('Errore durante il download del certificato');
    }
  };

  return (
    <div className="space-y-6" data-testid="rendicontazione-sociale-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Rendicontazione Sociale
          </h1>
          <p className="text-slate-500 mt-1">
            Report di impatto sociale e certificazioni volontari
          </p>
        </div>
      </div>

      <Tabs defaultValue="impatto" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="impatto" data-testid="tab-impatto">Report Impatto Sociale</TabsTrigger>
          <TabsTrigger value="certificati" data-testid="tab-certificati">Certificati Attività</TabsTrigger>
        </TabsList>

        {/* TAB: REPORT IMPATTO SOCIALE */}
        <TabsContent value="impatto" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#0055BB]" />
                Report di Impatto Sociale
              </CardTitle>
              <CardDescription>
                Genera un report riassuntivo delle attività di volontariato per la rendicontazione annuale
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="grid gap-4 md:grid-cols-4 mb-6 p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label>Anno di riferimento</Label>
                  <Select value={anno} onValueChange={setAnno}>
                    <SelectTrigger data-testid="select-anno">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Data Inizio</Label>
                  <Input
                    type="date"
                    value={dataInizio}
                    onChange={(e) => setDataInizio(e.target.value)}
                    data-testid="input-data-inizio"
                  />
                </div>
                <div>
                  <Label>Data Fine</Label>
                  <Input
                    type="date"
                    value={dataFine}
                    onChange={(e) => setDataFine(e.target.value)}
                    data-testid="input-data-fine"
                  />
                </div>
                <div className="flex items-end">
                  <Button onClick={loadReport} disabled={loading} className="w-full" data-testid="btn-genera-report">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <TrendingUp className="w-4 h-4 mr-2" />}
                    Genera Report
                  </Button>
                </div>
              </div>

              {/* Report Data */}
              {reportData && (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-3">
                          <Users className="w-8 h-8 text-blue-600" />
                          <div>
                            <p className="text-sm text-blue-600">Volontari Attivi</p>
                            <p className="text-2xl font-bold text-blue-700">{reportData.impatto_sociale?.volontari_attivi || 0}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-green-50 border-green-200">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-3">
                          <Calendar className="w-8 h-8 text-green-600" />
                          <div>
                            <p className="text-sm text-green-600">Attività Realizzate</p>
                            <p className="text-2xl font-bold text-green-700">{reportData.impatto_sociale?.attivita_realizzate || 0}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-purple-50 border-purple-200">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-3">
                          <Clock className="w-8 h-8 text-purple-600" />
                          <div>
                            <p className="text-sm text-purple-600">Ore Donate</p>
                            <p className="text-2xl font-bold text-purple-700">{reportData.impatto_sociale?.ore_donate || 0}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-pink-50 border-pink-200">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-3">
                          <Heart className="w-8 h-8 text-pink-600" />
                          <div>
                            <p className="text-sm text-pink-600">Beneficiari</p>
                            <p className="text-2xl font-bold text-pink-700">{reportData.impatto_sociale?.beneficiari_raggiunti || 0}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Detailed Stats */}
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Dati Generali Volontari</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-slate-500">Volontari Totali</span>
                            <span className="font-medium">{reportData.dati_generali?.volontari_totali || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Volontari Attivi</span>
                            <span className="font-medium text-green-600">{reportData.dati_generali?.volontari_attivi || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Volontari Occasionali</span>
                            <span className="font-medium">{reportData.dati_generali?.volontari_occasionali || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Volontari Non Occasionali</span>
                            <span className="font-medium">{reportData.dati_generali?.volontari_non_occasionali || 0}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Ore di Volontariato</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-slate-500">Totale Ore</span>
                            <span className="font-medium text-[#0055BB]">{reportData.ore_volontariato?.totale_ore || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Media per Volontario</span>
                            <span className="font-medium">{reportData.ore_volontariato?.media_ore_per_volontario || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Media per Attività</span>
                            <span className="font-medium">{reportData.ore_volontariato?.media_ore_per_attivita || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Volontari con Presenze</span>
                            <span className="font-medium">{reportData.ore_volontariato?.volontari_con_presenze || 0}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Download Buttons */}
                  <div className="flex gap-4 justify-center pt-4 border-t">
                    <Button onClick={() => downloadReport('pdf')} data-testid="download-pdf">
                      <Download className="w-4 h-4 mr-2" />
                      Scarica PDF
                    </Button>
                    <Button variant="outline" onClick={() => downloadReport('csv')} data-testid="download-csv">
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Scarica CSV
                    </Button>
                  </div>

                  {/* Report Info */}
                  <div className="text-center text-xs text-slate-500 pt-4">
                    <p>Report ID: {reportData.report_id}</p>
                    <p>Generato il: {new Date(reportData.generazione?.timestamp).toLocaleString('it-IT')}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: CERTIFICATI ATTIVITÀ */}
        <TabsContent value="certificati" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-[#0055BB]" />
                Certificato Attività Volontario
              </CardTitle>
              <CardDescription>
                Genera un certificato PDF che attesta le ore e le attività svolte da un volontario
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-w-xl mx-auto space-y-6">
                <div className="grid gap-4">
                  <div>
                    <Label>Seleziona Volontario</Label>
                    <Select value={selectedVolontario} onValueChange={setSelectedVolontario}>
                      <SelectTrigger data-testid="select-volontario-cert">
                        <SelectValue placeholder="Seleziona un volontario..." />
                      </SelectTrigger>
                      <SelectContent>
                        {volontari.map((vol) => (
                          <SelectItem key={vol.volontario_id} value={vol.volontario_id}>
                            {vol.cognome} {vol.nome} - {vol.codice_fiscale || 'N/D'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Anno di riferimento</Label>
                    <Select value={anno} onValueChange={setAnno}>
                      <SelectTrigger data-testid="select-anno-cert">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {years.map(y => (
                          <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button 
                  onClick={() => downloadCertificato(selectedVolontario)} 
                  disabled={!selectedVolontario}
                  className="w-full"
                  data-testid="btn-genera-certificato"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Genera Certificato PDF
                </Button>

                <div className="bg-slate-50 p-4 rounded-lg text-sm text-slate-600">
                  <p className="font-medium mb-2">Il certificato includerà:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Dati anagrafici del volontario</li>
                    <li>Periodo di riferimento</li>
                    <li>Numero di attività partecipate</li>
                    <li>Totale ore di volontariato</li>
                    <li>Elenco dettagliato delle attività svolte</li>
                    <li>Spazio per firma del legale rappresentante</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
